import { oc } from "@orpc/contract";
import { TimeFrameSchema } from "@domains/engagement/shared/timeFrame.ts";
import { FEEDBACK_METRIC_IDS } from "@domains/feedback/shared/feedbackTypes.ts";
import { SegmentAudienceRefusalSchema } from "@domains/segments/shared/segmentAudienceRefusal.ts";
import { ReportingScopeSchema } from "@packages/core/organization/scope.ts";
import { z } from "zod";
import { FEEDBACK_REPORT_SENTIMENTS, FEEDBACK_REPORT_TYPES } from "./feedbackReportFilters.ts";

export const FeedbackReportInputSchema = z.object({
    scope: ReportingScopeSchema.optional(),
    segmentId: z.string().optional(),
    timeFrame: TimeFrameSchema.optional(),
    start: z.iso.date().optional(),
    end: z.iso.date().optional(),
    type: z.enum(FEEDBACK_REPORT_TYPES).optional(),
    sentiment: z.enum(FEEDBACK_REPORT_SENTIMENTS).optional(),
    metricId: z.enum(FEEDBACK_METRIC_IDS).optional()
});
export type FeedbackReportInput = z.infer<typeof FeedbackReportInputSchema>;

export const FEEDBACK_REPORT_ERRORS = {
    UPSTREAM_FAILURE: {},
    AUDIENCE_REFUSED: { status: 400, data: z.object({ refusal: SegmentAudienceRefusalSchema }) }
};

const TOPIC_SIGNALS = ["Risk", "Trending", "New"] as const;
export const TopicSignalSchema = z.enum(TOPIC_SIGNALS);
export type TopicSignal = z.infer<typeof TopicSignalSchema>;

const TopicSignalRowSchema = z.object({
    topicId: z.string(),
    topicName: z.string(),
    signal: TopicSignalSchema,
    feedbackVolume: z.number().int()
});
export type TopicSignalRow = z.infer<typeof TopicSignalRowSchema>;

const TopicSignalsSchema = z.object({
    topics: z.array(TopicSignalRowSchema)
});
export type TopicSignals = z.infer<typeof TopicSignalsSchema>;

const ResponseOverviewSchema = z.object({
    totalCount: z.number().int(),
    totalCountDelta: z.number().int().nullable(),
    replyRate: z.number().min(0).max(1).nullable(),
    replyRateDelta: z.number().int().nullable(),
    unrepliedCount: z.number().int()
});
export type ResponseOverview = z.infer<typeof ResponseOverviewSchema>;

export const feedbackReportContract = {
    getTopicSignals: oc.route({ method: "GET", path: "/topic-signals" }).errors(FEEDBACK_REPORT_ERRORS).input(FeedbackReportInputSchema).output(TopicSignalsSchema),
    getResponseOverview: oc.route({ method: "GET", path: "/response-overview" }).errors(FEEDBACK_REPORT_ERRORS).input(FeedbackReportInputSchema).output(ResponseOverviewSchema)
};
